import {type Article, BillingRequestStatus, buildPlural} from "@spoferan/spoferan-ts-core";
import type {UserPreview} from "@spoferan/spoferan-ts-core";

export type ModelConfig = {
    type: string, // The type key of the model (required)
    config_key: string, // The route key of the model
    key_resolver: (item: any) => string | number, // Resolves the route key of the model
    name_resolver: (item: any) => string, // Resolves the displayable name of the model
    index_route_resolver: (parent?: any) => string,
    create_route_resolver: (parent?: any) => { path: string, query: any },
    show_route_resolver: (item: any, parent?: any) => string,
    edit_route_resolver: (item: any, parent?: any) => string,
    show_ep_resolver: ((item: any, parent?: any) => string) | null,
    index_ep_resolver: (parent?: any) => string,
    store_ep_resolver: (parent?: any) => string,
    update_ep_resolver: (item: any, parent?: any) => string,
    destroy_ep_resolver: (item: any, parent?: any) => string,
    destroy_method_resolver: (item: any, parent?: any) => string,
    destroy_data_resolver: (item: any, parent?: any) => any,
    lang_params_resolver: (item?: any, parent?: any) => any,

    // The label to display on the button to navigate to the creation form for the model
    create_label_resolver: (parent?: any) => string,

    // The label to display on the button to submit a create form for the model
    store_label_resolver: (parent?: any) => string,

    // The label to display on the button to submit an update form for the model
    update_label_resolver: (item?: any, parent?: any) => string,

    destroy_label_resolver: (item?: any, parent?: any) => string,
    permissions: {
        index: () => boolean,
        create: () => boolean,
        show: (item: any) => boolean,
        edit: (item: any) => boolean,
        destroy: (item: any) => boolean,
    }
}

export function useModelConfig(type: string, user: UserPreview | null = null, parentModelConfig = undefined): ModelConfig {
    const {$auth, $web} = useNuxtApp();
    const route = useRoute();
    const {t} = useI18n();

    const modelUser: UserPreview = user ?? $auth.user;

    const hasPermission = $auth.user && modelUser.id === $auth.user.id;
    const pluralType = buildPlural(type);
    const configKey = parentModelConfig ? `${parentModelConfig.config_key}.${pluralType}` : pluralType;

    return {
        type: type,
        config_key: configKey,
        key_resolver: (item) => item.id,
        name_resolver: (item) => item.name ? item.name : (item.display_name ? item.display_name : item.title),
        index_route_resolver: function (parent = undefined) {
            return $web(`my.profile.manage.${configKey}.index`, parent ? [parentModelConfig.key_resolver(parent)] : [])
        },
        create_route_resolver: function (parent = undefined) {
            return {
                path: $web(`my.profile.manage.${configKey}.create`, parent ? [parentModelConfig.key_resolver(parent)] : []),
                query: {...route.query, redirect: route.fullPath}
            }
        },
        show_route_resolver: function (item, parent = undefined) {
            return $web(`my.profile.manage.${configKey}.show`, parent ? [parentModelConfig.key_resolver(parent), this.key_resolver(item)] : [this.key_resolver(item)])
        },
        edit_route_resolver: function (item, parent = undefined) {
            return $web(`my.profile.manage.${configKey}.edit`, parent ? [parentModelConfig.key_resolver(parent), this.key_resolver(item)] : [this.key_resolver(item)])
        },
        show_ep_resolver: null,
        index_ep_resolver: null,
        store_ep_resolver: function (parent = undefined) {
            return this.index_ep_resolver ? this.index_ep_resolver(parent) : null;
        },
        update_ep_resolver: function (item, parent = undefined) {
            return this.show_ep_resolver ? this.show_ep_resolver(item, parent) : null;
        },
        destroy_ep_resolver: function (item, parent = undefined) {
            return this.update_ep_resolver ? this.update_ep_resolver(item, parent) : null;
        },
        destroy_method_resolver: (item, parent = undefined) => 'DELETE',
        destroy_data_resolver: (item, parent = undefined) => {
        },
        create_label_resolver: (parent?: any) => t('action.create'),
        store_label_resolver: function (parent?: any) {
            return this.create_label_resolver(parent);
        },
        update_label_resolver: () => t('action.save'),
        destroy_label_resolver: () => t('action.destroy'),
        lang_params_resolver: function (item = undefined, parent = undefined) {
            const params = {
                user: modelUser.display_name,
            };
            if (parent && parentModelConfig) {
                params[parentModelConfig.type] = parentModelConfig.name_resolver(parent)
            }
            if (item) {
                params[type] = this.name_resolver(item)
            }

            return params;
        },
        permissions: {
            index: () => hasPermission,
            view: () => hasPermission,
            create: () => hasPermission,
            update: () => hasPermission,
            delete: () => hasPermission,
        }
    }
}

export const useBillingRequestModelConfig = (user: UserPreview | null = null): ModelConfig => {
    const {$auth} = useNuxtApp();
    const {t} = useI18n();

    const modelUser: UserPreview = user ?? $auth.user;
    const hasPermission = $auth.user && modelUser.id === $auth.id;

    return {
        ...useModelConfig('billing_request', modelUser),
        name_resolver: (item) => item.description,
        create_label_resolver: () => t('action.submit'),
        update_label_resolver: () => t('action.submit'),
        index_ep_resolver: () => `/v1/athletes/${modelUser.id}/billing-requests`,
        update_ep_resolver: (item) => `/v1/athletes/${modelUser.id}/billing-requests/${item.id}`,
        permissions: {
            index: () => hasPermission,
            view: () => hasPermission,
            create: () => hasPermission,
            update: (item) => ![BillingRequestStatus.Pending, BillingRequestStatus.Paid].includes(item.status.value),
            delete: (item) => hasPermission && ![BillingRequestStatus.Pending, BillingRequestStatus.Paid].includes(item.status.value),
        }
    }
}

export const useArticleModelConfig = (user: UserPreview | null = null): ModelConfig => {
    const {$auth} = useNuxtApp();
    const {t} = useI18n();

    const modelUser: UserPreview = user ?? $auth.user;
    const hasPermission = $auth.user && modelUser.id === $auth.id;

    return {
        ...useModelConfig('article', modelUser),
        index_ep_resolver: () => `/v1/athletes/${modelUser.id}/articles`,
        store_ep_resolver: () => `/v1/articles`,
        update_ep_resolver: (item: Article) => `/v1/articles/${item.id}`,
        show_route_resolver: (item: Article) => `/${item.type.value}/${item._key}?debug=preview,nocache`,
        edit_route_resolver: (item: Article) => item.club ? `https://${item.club._key}.spoferan.com/articles/${item._key}/edit` : `/my/profile/manage/blog/${item._key}/edit`,
        store_label_resolver: () => t('action.publish'),
        update_label_resolver: (item: Article) => item.status === 'published' ? t('action.save') : t('action.publish'),
        permissions: {
            index: () => hasPermission,
            view: () => true,
            create: () => hasPermission,
            update: () => hasPermission,
            delete: () => hasPermission,
        }
    }
}